import {
  ButtonList,
  DropdownButton,
  DropdownButtonContext,
  IconNames,
} from '@dixa/lib-shared-habla';
import type { SearchFilterCondition } from 'lib-shared-types-api';
import { Fragment, memo, useMemo } from 'react';

import { useSearchFilters } from '../../../../searchFilters.context';

type ConditionActionsProps = {
  index: number;
  condition: SearchFilterCondition;
};

type ConditionAction = {
  text: string;
  icon: IconNames;
  divider?: boolean;
  onClick: VoidFunction;
};

/**
 * Represents a component that can be used to add, duplicate or remove
 * conditions for a filter
 */
export const ConditionActions = memo(function ConditionActions({
  index,
  condition,
}: ConditionActionsProps): JSX.Element {
  const { copyCondition, removeCondition } = useSearchFilters();

  const actions: ConditionAction[] = useMemo(
    () => [
      {
        text: 'Add another',
        icon: 'plus',
        onClick: () => copyCondition(index, condition, { fullCopy: false }),
      },
      {
        text: 'Duplicate',
        icon: 'copy',
        onClick: () => copyCondition(index, condition, { fullCopy: true }),
      },
      {
        text: 'Remove',
        icon: 'trash',
        divider: true,
        onClick: () => removeCondition(index),
      },
    ],
    [condition, copyCondition, index, removeCondition],
  );

  return (
    <DropdownButton
      icon="dots-v"
      buttonProps={{ variant: 'tertiary' }}
      fixed
      noArrow
      dropdown={
        <DropdownButtonContext.Consumer>
          {({ toggleDropdown }) => (
            <ButtonList>
              {actions.map(
                ({ text, icon, divider = false, onClick }, actionIndex) => (
                  <Fragment key={actionIndex}>
                    {divider && <ButtonList.Divider />}

                    <ButtonList.Item
                      icon={icon}
                      onClick={() => {
                        onClick();
                        toggleDropdown();
                      }}
                    >
                      {text}
                    </ButtonList.Item>
                  </Fragment>
                ),
              )}
            </ButtonList>
          )}
        </DropdownButtonContext.Consumer>
      }
    />
  );
});
